import type { Driver } from 'driver.js';
import type { TourDefinition } from './types';

export const TOUR_DEFINITIONS: TourDefinition[] = [
  {
    id: 'onboarding:plano-adaptativo-hub',
    route: '/plano-adaptativo/:id/hub',
    flow: 'onboarding',
    priority: 0,
    steps: [
      {
        element: '[data-tour="day-activity-card"]',
        popover: {
          title: 'Atividades do Dia',
          description:
            'Aqui você acompanha tudo o que foi planejado para o seu dia. Veja o que já começou, o que ainda falta fazer e o que já foi concluído.',
        },
      },
      {
        element: '[data-tour="progress-card"]',
        popover: {
          title: 'Progresso',
          description:
            'Aqui você acompanha seu progresso ao longo do tempo, com uma visão semanal, mensal ou anual das suas atividades.',
        },
      },
      {
        element: '[data-tour="study-time-progress"]',
        popover: {
          title: 'Status da Semana',
          description:
            'Aqui você acompanha o andamento da sua semana e entende se está em dia, adiantado ou com atividades pendentes.',
        },
      },
      {
        element: '[data-tour="discipline-report"]',
        popover: {
          title: 'Resumo das Disciplinas',
          description:
            'Nesta área, você analisa o andamento de cada disciplina e acompanha sua evolução ao longo da semana, do mês ou do ano.',
        },
      },
      {
        element: '[data-tour="week-activity-schedule"]',
        popover: {
          title: 'Semana 01',
          description:
            'Aqui você gerencia sua semana de estudos com mais flexibilidade, movendo os cards entre os dias da forma que fizer mais sentido para você.',
        },
      },
      {
        element: '[data-tour="week-selector"]',
        popover: {
          title: 'Seleção de Semanas',
          description:
            'Aqui você navega entre as semanas do seu plano e visualiza ou adianta seus conteúdos com mais facilidade.',
        },
      },
      {
        element: '[data-tour="incomplete-tasks-toggle"]',
        popover: {
          title: 'Conteúdos não concluídos',
          description:
            'Ative este filtro para ver os conteúdos da semana que ainda faltam concluir.',
        },
      },
      {
        element: '[data-tour="ssr-button"]',
        popover: {
          title: 'Simulado Semanal de Revisão (SSR)',
          description:
            'Ao clicar aqui, você será direcionado para a área do Simulado Semanal de Revisão.',
        },
      },
      {
        element: '[data-tour="manage-subjects-button"]',
        popover: {
          title: 'Gerenciar assuntos',
          description:
            'Aqui você pode adicionar disciplinas, incluir novos assuntos ou remover conteúdos do dia que escolheu.',
        },
      },
    ],
  },
  {
    id: 'onboarding:gerenciar-assuntos',
    route: '/plano-adaptativo/:id/disciplinas',
    flow: 'onboarding',
    priority: 1,
    condition: () => new URLSearchParams(window.location.search).has('editMode'),
    steps: [
      {
        element: '[data-tour="manage-schedule"]',
        popover: {
          title: 'Conteúdo das Disciplinas',
          description:
            'Aqui você gerencia o dia selecionado, com a opção de adicionar disciplinas, incluir ou remover assuntos e acompanhar o progresso de cada disciplina.',
        },
      },
      {
        element: '[data-tour="accordion-toggle"]',
        popover: {
          title: 'Expandir/Minimizar',
          description:
            'Aqui você expande ou minimiza a aba da disciplina',
          onNextClick: (_el: Element | undefined, _step: unknown, { driver }: { driver: Driver }) => {
            if (!document.querySelector('[data-tour="edit-button"]')) {
              const toggle = document.querySelector('[data-tour="accordion-toggle"]');
              const btn = toggle?.closest('button') ?? toggle;
              if (btn instanceof HTMLElement) btn.click();
              setTimeout(() => driver.moveNext(), 300);
            } else {
              driver.moveNext();
            }
          },
        },
      },
      {
        element: '[data-tour="edit-button"]',
        popover: {
          title: 'Editar',
          description:
            'Use este botão para selecionar os assuntos que deseja remover da disciplina.',
        },
      },
      {
        element: '[data-tour="add-subject-button"]',
        popover: {
          title: 'Adicionar Assunto',
          description:
            'Aqui você adiciona um assunto na disciplina selecionada',
        },
      },
      {
        element: '[data-tour="add-discipline-button"]',
        popover: {
          title: 'Adicionar Disciplina',
          description:
            'Aqui você adiciona uma disciplina no dia selecionado',
        },
      },
    ],
  },
  {
    id: 'onboarding:adicionar-assunto',
    route: '/plano-adaptativo/:id/disciplinas',
    flow: 'onboarding',
    priority: 2,
    condition: () => new URLSearchParams(window.location.search).has('editMode'),
    steps: [
      {
        element: '[data-tour="search-subject-input"]',
        popover: {
          title: 'Pesquisar Assunto',
          description:
            'Aqui você busca os assuntos que deseja adicionar aos seus estudos do dia.',
          onNextClick: (_el: Element | undefined, _step: unknown, { driver }: { driver: Driver }) => {
            if (!document.querySelector('[data-tour="subject-week-column"]')) {
              const accordion = document.querySelector('[data-tour="subject-module-accordion"] button');
              if (accordion instanceof HTMLElement) accordion.click();
              setTimeout(() => driver.moveNext(), 300);
            } else {
              driver.moveNext();
            }
          },
        },
      },
      {
        element: '[data-tour="subject-week-column"]',
        popover: {
          title: 'Semana',
          description:
            'Quando o assunto não mostrar a semana, isso indica que ele ainda não foi incluído no seu cronograma.',
        },
      },
    ],
  },
  {
    id: 'onboarding:disciplinas-visao-geral',
    route: '/plano-adaptativo/:id/disciplinas',
    flow: 'onboarding',
    priority: 0,
    condition: () => !new URLSearchParams(window.location.search).has('editMode'),
    steps: [
      {
        element: '[data-tour="disciplines-overview"]',
        popover: {
          title: 'Conteúdo das Disciplinas',
          description:
            'Aqui você confere, semana a semana, quais disciplinas e assuntos serão abordados.',
        },
      },
    ],
  },
  {
    id: 'onboarding:cronograma-completo',
    route: '/plano-adaptativo/:id/completo',
    flow: 'onboarding',
    priority: 0,
    steps: [
      {
        element: '[data-tour="full-schedule"]',
        popover: {
          title: 'Cronograma Completo',
          description:
            'Aqui você visualiza seu cronograma completo por disciplinas ou por semanas.',
        },
      },
      {
        element: '[data-tour="discipline-selector"]',
        popover: {
          title: 'Disciplinas',
          description:
            'Aqui você navega entre as disciplinas do seu Cronograma',
        },
      },
      {
        element: '[data-tour="module-card"]',
        popover: {
          title: 'Módulos',
          description:
            'Aqui você visualiza os Módulos, o progresso, e sua porcentagem de acertos',
          onNextClick: (_el: Element | undefined, _step: unknown, { driver }: { driver: Driver }) => {
            if (!document.querySelector('[data-tour="module-subjects-table"]')) {
              const toggle = document.querySelector('[data-tour="module-toggle"]');
              const btn = toggle?.closest('button') ?? toggle;
              if (btn instanceof HTMLElement) btn.click();
              setTimeout(() => driver.moveNext(), 300);
            } else {
              driver.moveNext();
            }
          },
        },
      },
      {
        element: '[data-tour="module-subjects-table"]',
        popover: {
          title: 'Assuntos',
          description:
            'Aqui você visualiza os assuntos do módulo, a semana de estudo e seu progresso em videoaulas, materiais e taxa de acerto.',
        },
      },
    ],
  },
];
